/** @jsx jsx */

import React, { Fragment as _, useState, useRef } from 'react';
import { css, jsx } from '@emotion/core';
import getStore from 'services/getStore';
import { colors } from 'styles/variables';
import EditorBtn from 'components/UI/EditorBtn';
import { Block } from 'factories/block';
import { AudioUploadStatus } from './AudioUploadStatus';
import {
  uploadToD3,
  createUpload,
} from 'services/graphQLFileIO/graphqlCreateUpload';
import { useSelector } from 'react-redux';
import { State } from 'store/store';

const store = getStore();

const { lGrey0 } = colors;

interface IAudioUploader {
  audioSrc: string;
  audioId?: string | number;
  setBlock: (block, data) => void;
  block: Block;
  setUploadError?: (err: string) => void;
}

const AudioUploader = ({
  audioSrc,
  audioId,
  setBlock,
  block,
  setUploadError,
}: IAudioUploader) => {
  //

  const projectId = useSelector((state: State) => {
    return state?.projectId;
  });

  const [uploadStatus, setUploadStatus] = useState<{
    progress: number | undefined;
    uploading: boolean;
    processing: boolean;
    ready: boolean;
    error: boolean;
  }>({
    progress: undefined,
    uploading: false,
    processing: false,
    ready: false,
    error: false,
  });

  const audioFileInput = useRef<any>(null);

  const uploadToD3Ref = useRef(uploadToD3());
  const { abort, upload } = uploadToD3Ref?.current;

  let audioSrcFileName: string[] | string = audioSrc
    ? audioSrc.split(/\//g)
    : [];
  if (audioSrcFileName.length) {
    audioSrcFileName = audioSrcFileName[audioSrcFileName.length - 1];
  }

  return (
    <_>
      <input
        ref={audioFileInput}
        type="file"
        style={{
          display: 'none',
        }}
        onChange={async e => {
          e.persist();

          const targetFiles = (e.target as any).files;

          const formData = new FormData();
          const files = Array.from(targetFiles);

          let isMP3;

          files.forEach((file: any) => {
            const { name } = file;

            const ext = name.split(/\./g);
            isMP3 = ['mp3', 'MP3'].includes(ext[ext.length - 1]);

            if (!isMP3) {
              return;
            }

            formData.append('file', file);
            formData.append('name', file.name);
            formData.append('project_id', projectId as string);
          });

          if (!isMP3) {
            // console.warn('The selected file is not an mp3. Aborting process.');
            audioFileInput.current.value = null;

            alert(
              'The file you are trying to upload is not supported. Ensure the file type is an mp3.'
            );

            return;
          }

          const file = files?.[0];

          setUploadStatus({
            ...uploadStatus,
            uploading: true,
          });

          let signedId;

          try {
            signedId = await upload(file, percentComplete => {
              setUploadStatus(uploadStatus => ({
                ...uploadStatus,
                progress: percentComplete,
                ...(percentComplete >= 99 && {
                  processing: true,
                }),
              }));
            });
          } catch (err) {
            console.error('ERROR UPLOADING TO D3', err);
            setUploadError?.(
              'There was an error uploading your audio. Please try again.'
            );
          }

          try {
            const { status, data } = await createUpload({
              signedId,
              file,
              type: 'Audio',
            } as any);

            const { id, url } = data?.file || {};

            if (url && id) {
              // we have finished and are ready to go
              setUploadStatus(uploadStatus => ({
                ...uploadStatus,
                ready: true,
              }));

              //

              requestAnimationFrame(() => {
                audioFileInput.current.value = null;
                //
                setBlock(block, {
                  audioSrc: url,
                  audioIdV2: id,
                });
              });
            } else {
              console.warn('NO URL and/or ID EXISTS ON DATA :', { url, id });
              //
              setUploadStatus(uploadStatus => ({
                ...uploadStatus,
                error: true,
              }));
            }
          } catch (err) {
            console.error(`ERROR GETTING GRAPHQL UPLOAD DATA`, err);
          }
        }}
      />

      <div
        css={css({
          display: 'flex',
          alignItems: 'center',
        })}
      >
        <EditorBtn
          label="Insert audio"
          style={{
            width: '175px',
            margin: '0 15px 0 0',
            border: `1px solid ${lGrey0}`,
            background: '#fff',
            color: lGrey0,
            fontSize: 11,

            hover: {
              color: '#fff',
              background: '#28292b',
            },
          }}
          css={css`
            flex-shrink: 0;
            min-height: 30px;
          `}
          onClick={() => {
            audioFileInput.current.click();
          }}
        />
      </div>

      {uploadStatus.uploading && (
        <AudioUploadStatus
          uploadStatus={uploadStatus}
          onCancel={() => {
            //
            abort();
            audioFileInput.current.value = null;
            //
          }}
          onClose={() => {
            audioFileInput.current.value = null;

            setUploadStatus({
              progress: 0,
              uploading: false,
              processing: false,
              ready: false,
              error: false,
            });
          }}
        />
      )}
    </_>
  );
};

export { AudioUploader };
